import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircle2, XCircle, Loader2, LayoutDashboard, Printer, RefreshCw,
  Clock, FileWarning, User, Mail, Phone, CreditCard, Calendar,
  AlertTriangle, Sparkles, Download, ReceiptText, FileText,
} from 'lucide-react';

// Public receipt API — hosted by ASB606 Admin.
// NOTE: This endpoint is public (no auth), CORS is open, and returns only
// customer-safe data (no commissions, no internal receipts).
const RECEIPT_API = 'https://asb606.com/api/public/receipt';
const POLL_INTERVAL_MS = 5000;
const POLL_MAX_ATTEMPTS = 5;

// iter-291 — Unified render-as-a-service template. Points at our own
// authenticated proxy so the merchant key never leaves the backend.
const RENDER_API = `${process.env.REACT_APP_BACKEND_URL}/api/render/receipt`;

// CloudGM Business brand — Cyan + Orange (matches tailwind primary/secondary).
const BRAND = {
  primary: '#38BDF8',       // CloudGM Cyan
  primaryDark: '#0284C7',
  primaryLight: '#E0F2FE',
  accent: '#F97316',        // CloudGM Orange
  accentDark: '#C2410C',
  accentLight: '#FFEDD5',
  nameAr: 'CloudGM Business',
  nameEn: 'CloudGM Business',
};

const t = (lang, ar, en) => (lang === 'ar' ? ar : en);

// ─────────────────────────────────────────────────────────────────────
// Confetti burst — pure CSS/JS, no deps. Fires once on paid success.
// ─────────────────────────────────────────────────────────────────────
function ConfettiBurst({ active }) {
  if (!active) return null;
  const colors = ['#38BDF8', '#F97316', '#22C55E', '#EAB308', '#A855F7'];
  const pieces = Array.from({ length: 36 }, (_, i) => ({
    i,
    left: Math.random() * 100,
    delay: Math.random() * 0.4,
    rot: Math.random() * 360,
    dur: 2.2 + Math.random() * 1.6,
    color: colors[i % colors.length],
    size: 6 + Math.random() * 6,
  }));
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden z-40" aria-hidden data-testid="confetti-burst">
      {pieces.map((p) => (
        <span
          key={p.i}
          className="absolute top-0 block rounded-sm"
          style={{
            left: `${p.left}%`,
            width: `${p.size}px`,
            height: `${p.size * 1.4}px`,
            background: p.color,
            transform: `rotate(${p.rot}deg)`,
            animation: `cgm-confetti-fall ${p.dur}s ${p.delay}s cubic-bezier(.22,.9,.4,1) forwards`,
            opacity: 0.9,
          }}
        />
      ))}
      <style>{`
        @keyframes cgm-confetti-fall {
          0%   { transform: translate3d(0,-20vh,0) rotate(0deg);   opacity: 0; }
          8%   { opacity: 1; }
          100% { transform: translate3d(0,110vh,0) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const invoiceId = searchParams.get('inv') || searchParams.get('invoice') || '';
  const langParam = searchParams.get('lang');
  const [lang, setLang] = useState(
    langParam === 'en' ? 'en' : langParam === 'ar' ? 'ar' : (document.documentElement.dir === 'rtl' ? 'ar' : 'ar')
  );

  const [state, setState] = useState('loading'); // loading | paid | partially_paid | unpaid_polling | cancelled | not_found | error | missing
  const [receipt, setReceipt] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const confettiFired = useRef(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const isRTL = lang === 'ar';

  const fetchReceipt = useCallback(async () => {
    if (!invoiceId) {
      setState('missing');
      return;
    }
    try {
      const { data } = await axios.get(`${RECEIPT_API}/${encodeURIComponent(invoiceId)}`, { timeout: 15000 });
      setReceipt(data);

      const status = (data.status || '').toLowerCase();
      if (status === 'paid') setState('paid');
      else if (status === 'partially_paid') setState('partially_paid');
      else if (status === 'cancelled' || status === 'expired') setState('cancelled');
      else if (status === 'unpaid' || status === 'pending') setState('unpaid_polling');
      else setState('error');

      // iter-255 — Best-effort mirror update on our own DB. Backend
      // re-fetches the receipt itself (so a user cannot self-mark
      // paid). Failures are silent — ASB606's periodic mirror push
      // will still eventually correct the state.
      const token = localStorage.getItem('business_token');
      if (token && (status === 'paid' || status === 'partially_paid')) {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        axios.post(
          `${backendUrl}/api/agent-invoices/${encodeURIComponent(invoiceId)}/mirror-status`,
          {},
          { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 },
        ).catch(() => { /* silent: ASB606 sync will fix it */ });
      }
    } catch (err) {
      const code = err?.response?.status;
      if (code === 404) setState('not_found');
      else {
        setErrorMessage(err?.message || 'Network error');
        setState('error');
      }
    }
  }, [invoiceId]);

  useEffect(() => {
    fetchReceipt();
  }, [fetchReceipt]);

  // Poll for pending payments
  useEffect(() => {
    if (state !== 'unpaid_polling') return;
    if (pollAttempts >= POLL_MAX_ATTEMPTS) {
      setState('cancelled'); // stop after too many attempts — treat as cancelled/timeout
      return;
    }
    const timer = setTimeout(async () => {
      setPollAttempts(a => a + 1);
      await fetchReceipt();
    }, POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [state, pollAttempts, fetchReceipt]);

  // Fire confetti once on first paid/partial state.
  useEffect(() => {
    if ((state === 'paid' || state === 'partially_paid') && !confettiFired.current) {
      confettiFired.current = true;
      setShowConfetti(true);
      const t = setTimeout(() => setShowConfetti(false), 4200);
      return () => clearTimeout(t);
    }
  }, [state]);

  const goDashboard = () => navigate('/dashboard');
  const goPayments = () => navigate('/payments');

  const printReceipt = () => window.print();

  return (
    <div
      dir={isRTL ? 'rtl' : 'ltr'}
      className="min-h-screen py-8 px-4 sm:px-6 print:bg-white relative"
      style={{
        background: 'linear-gradient(135deg, #F0F9FF 0%, #FFF7ED 50%, #FEFCE8 100%)',
      }}
      data-testid="payment-success-page"
    >
      <ConfettiBurst active={showConfetti} />

      {/* Language switch */}
      <div className="max-w-3xl mx-auto flex justify-end mb-4 print:hidden">
        <button
          onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
          className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white/80 backdrop-blur border border-gray-200 text-gray-700 hover:bg-white transition-colors"
          data-testid="lang-toggle"
        >
          {lang === 'ar' ? 'English' : 'العربية'}
        </button>
      </div>

      <div className="max-w-3xl mx-auto relative z-10">
        {/* MAIN CARD */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden print:shadow-none print:rounded-none animate-in fade-in zoom-in-95 duration-500" data-testid="receipt-card">

          {/* ===== Loading ===== */}
          {state === 'loading' && (
            <div className="p-12 text-center" data-testid="state-loading">
              <Loader2 className="h-14 w-14 mx-auto animate-spin" style={{ color: BRAND.primary }} />
              <p className="mt-4 text-gray-500">
                {t(lang, 'جارٍ التحقق من الفاتورة...', 'Verifying your invoice...')}
              </p>
            </div>
          )}

          {/* ===== Missing invoice_id ===== */}
          {state === 'missing' && (
            <ErrorState
              lang={lang}
              icon={<FileWarning />}
              titleAr="رقم الفاتورة مفقود"
              titleEn="Invoice number missing"
              descAr="لم يتم توفير رقم فاتورة صالح في الرابط."
              descEn="No valid invoice number was provided in the URL."
              onDashboard={goDashboard}
              onPayments={goPayments}
              testId="state-missing"
            />
          )}

          {/* ===== Not found (404) ===== */}
          {state === 'not_found' && (
            <ErrorState
              lang={lang}
              icon={<FileWarning />}
              titleAr="الفاتورة غير موجودة"
              titleEn="Invoice not found"
              descAr={`لم نعثر على فاتورة برقم "${invoiceId}". تأكد من الرابط أو تواصل مع الدعم.`}
              descEn={`We couldn't find an invoice matching "${invoiceId}". Please verify the link or contact support.`}
              onDashboard={goDashboard}
              onPayments={goPayments}
              testId="state-not-found"
            />
          )}

          {/* ===== Network / server error ===== */}
          {state === 'error' && (
            <ErrorState
              lang={lang}
              icon={<AlertTriangle />}
              titleAr="حدث خطأ مؤقت"
              titleEn="A temporary error occurred"
              descAr={errorMessage || 'حاول لاحقاً أو تواصل مع الدعم.'}
              descEn={errorMessage || 'Please try again later or contact support.'}
              onDashboard={goDashboard}
              onPayments={goPayments}
              onRetry={() => { setState('loading'); setPollAttempts(0); fetchReceipt(); }}
              testId="state-error"
            />
          )}

          {/* ===== Cancelled / expired ===== */}
          {state === 'cancelled' && (
            <ErrorState
              lang={lang}
              icon={<XCircle />}
              titleAr="تم إلغاء الفاتورة"
              titleEn="Invoice cancelled"
              descAr="الفاتورة أُلغيت أو انتهت صلاحيتها. يمكنك المحاولة مرة أخرى."
              descEn="This invoice has been cancelled or expired. You can retry."
              onDashboard={goDashboard}
              onPayments={goPayments}
              onRetry={() => { setState('loading'); setPollAttempts(0); fetchReceipt(); }}
              retryLabelAr="محاولة أخرى"
              retryLabelEn="Retry"
              variant="warning"
              testId="state-cancelled"
            />
          )}

          {/* ===== Pending / polling ===== */}
          {state === 'unpaid_polling' && (
            <div className="p-10 sm:p-12 text-center" data-testid="state-polling">
              <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center" style={{ background: BRAND.primaryLight }}>
                <Clock className="h-10 w-10 animate-pulse" style={{ color: BRAND.primary }} />
              </div>
              <h2 className="mt-5 text-2xl font-bold text-gray-800">
                {t(lang, 'الدفع قيد المعالجة...', 'Payment is being processed...')}
              </h2>
              <p className="mt-2 text-sm text-gray-500 leading-relaxed">
                {t(lang,
                  'قد يستغرق الأمر بضع ثوانٍ. يرجى عدم إغلاق هذه الصفحة.',
                  'This may take a few seconds. Please do not close this page.'
                )}
              </p>
              <div className="mt-6 flex items-center justify-center gap-2 text-xs text-gray-400">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>{t(lang, `المحاولة ${pollAttempts + 1} من ${POLL_MAX_ATTEMPTS}`, `Attempt ${pollAttempts + 1} of ${POLL_MAX_ATTEMPTS}`)}</span>
              </div>
            </div>
          )}

          {/* ===== SUCCESS: paid / partially_paid ===== */}
          {(state === 'paid' || state === 'partially_paid') && receipt && (
            <ReceiptContent
              receipt={receipt}
              lang={lang}
              partial={state === 'partially_paid'}
              onDashboard={goDashboard}
              onPayments={goPayments}
              onPrint={printReceipt}
            />
          )}
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-gray-400 mt-6 print:hidden">
          {t(lang,
            'هذه الصفحة تعرض إيصالك بشكل آمن — لا يمكن تعديل بيانات الفاتورة.',
            'This page displays your receipt securely — invoice data cannot be modified.'
          )}
        </p>
      </div>
    </div>
  );
}

// ===================== Sub-components =====================

function ReceiptContent({ receipt, lang, partial, onDashboard, onPayments, onPrint }) {
  const isRTL = lang === 'ar';
  const currency = receipt.currency || 'AED';
  const currencyLabel = lang === 'ar' ? 'درهم' : currency;

  // iter-291 — Official unified template (Admin Receipt Studio).
  // Any change in Studio reflects here instantly without a redeploy.
  const [officialHtml, setOfficialHtml] = useState(null);
  const [officialState, setOfficialState] = useState('idle'); // idle | loading | ready | error
  const [showOfficial, setShowOfficial] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const loadOfficial = useCallback(async () => {
    if (officialHtml) return officialHtml;
    setOfficialState('loading');
    try {
      const token = localStorage.getItem('business_token');
      if (!token) throw new Error('not signed in');
      const { data } = await axios.post(
        RENDER_API,
        { invoice_id: receipt.invoice_no || receipt.invoice_id, lang },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 },
      );
      const html = data?.html || null;
      setOfficialHtml(html);
      setOfficialState(html ? 'ready' : 'error');
      return html;
    } catch {
      setOfficialState('error');
      return null;
    }
  }, [officialHtml, receipt, lang]);

  const openOfficial = useCallback(async () => {
    setShowOfficial(true);
    await loadOfficial();
  }, [loadOfficial]);

  // Download as PDF — opens the official HTML in a new window and
  // triggers the browser's print-to-PDF dialog. Works everywhere, no
  // extra deps required.
  const downloadPdf = useCallback(async () => {
    if (downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const html = officialHtml || await loadOfficial();
      if (!html) return;
      const w = window.open('', '_blank');
      if (!w) {
        // popup blocked — fall back to the embedded print path.
        onPrint?.();
        return;
      }
      w.document.open();
      w.document.write(html);
      w.document.close();
      // Give the window time to parse & render before invoking print.
      setTimeout(() => { try { w.focus(); w.print(); } catch { /* popup blocked */ } }, 500);
    } finally {
      setDownloadingPdf(false);
    }
  }, [officialHtml, loadOfficial, downloadingPdf, onPrint]);

  const printOfficial = () => {
    if (!officialHtml) return;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.open();
    w.document.write(officialHtml);
    w.document.close();
    setTimeout(() => { try { w.focus(); w.print(); } catch { /* popup blocked */ } }, 400);
  };

  const platformName = lang === 'ar' ? (receipt.platform_name_ar || BRAND.nameAr) : (receipt.platform_name_en || BRAND.nameEn);
  const paymentMethodLabel = lang === 'ar'
    ? (receipt.payment_method_ar || receipt.payment_method || '—')
    : (receipt.payment_method || '—');

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(lang === 'ar' ? 'ar-AE' : 'en-US', {
        year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch { return iso; }
  };

  return (
    <div data-testid="state-success">
      {/* ===== Header ===== */}
      <div className="p-8 sm:p-10 text-white text-center relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryDark} 60%, ${BRAND.accent} 140%)` }}>
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-white/10 blur-2xl" />
        <div className="relative">
          {/* Animated success ring */}
          <div className="relative w-24 h-24 mx-auto">
            <div className="absolute inset-0 rounded-full bg-white/20 animate-ping" style={{ animationDuration: '1.6s', animationIterationCount: 2 }} />
            <div className="absolute inset-0 rounded-full bg-white/20 backdrop-blur border-2 border-white/50 flex items-center justify-center animate-in zoom-in-50 duration-500">
              <CheckCircle2 className="h-12 w-12" data-testid="success-icon" />
            </div>
          </div>
          <h1 className="mt-5 text-2xl sm:text-3xl font-bold tracking-tight animate-in fade-in slide-in-from-bottom-2 duration-700" data-testid="receipt-headline">
            {partial
              ? (lang === 'ar' ? 'تم الدفع جزئياً' : 'Partially Paid')
              : (lang === 'ar' ? 'تم الدفع بنجاح' : 'Payment Successful')}
          </h1>
          <p className="mt-1 text-white/85 text-sm">{platformName}</p>

          <div className="mt-5 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/20 backdrop-blur border border-white/30 text-sm font-mono font-bold" data-testid="receipt-invoice-no">
            <ReceiptText className="h-3.5 w-3.5 opacity-80" />
            <span className="opacity-80">{lang === 'ar' ? 'الإيصال' : 'Receipt'}</span>
            <span>#{receipt.invoice_no}</span>
          </div>
        </div>
      </div>

      {/* ===== Status Badges ===== */}
      <div className="px-6 sm:px-10 py-4 flex flex-wrap items-center justify-center gap-2 border-b border-dashed border-gray-200">
        {partial ? (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700 border border-amber-200" data-testid="badge-partial">
            <Clock className="h-3.5 w-3.5" />
            {lang === 'ar' ? 'مدفوعة جزئياً' : 'Partially Paid'}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold text-sky-700 border" style={{ background: BRAND.primaryLight, borderColor: '#BAE6FD' }} data-testid="badge-paid">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {lang === 'ar' ? 'مدفوع' : 'Paid'}
          </span>
        )}
        {paymentMethodLabel && paymentMethodLabel !== '—' && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200" data-testid="badge-method">
            <CreditCard className="h-3.5 w-3.5" />
            {paymentMethodLabel}
          </span>
        )}
      </div>

      {/* ===== Customer info ===== */}
      {receipt.customer && (
        <div className="px-6 sm:px-10 py-5 border-b border-gray-100">
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">
            {lang === 'ar' ? 'بيانات العميل' : 'Customer Details'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {receipt.customer.name && (
              <InfoField
                icon={<User className="h-3.5 w-3.5" />}
                label={lang === 'ar' ? 'الاسم' : 'Name'}
                value={receipt.customer.name}
                testId="customer-name"
              />
            )}
            {receipt.customer.email && (
              <InfoField
                icon={<Mail className="h-3.5 w-3.5" />}
                label={lang === 'ar' ? 'البريد' : 'Email'}
                value={receipt.customer.email}
                dir="ltr"
                testId="customer-email"
              />
            )}
            {receipt.customer.phone && (
              <InfoField
                icon={<Phone className="h-3.5 w-3.5" />}
                label={lang === 'ar' ? 'الهاتف' : 'Phone'}
                value={receipt.customer.phone}
                dir="ltr"
                testId="customer-phone"
              />
            )}
          </div>
        </div>
      )}

      {/* ===== Services table ===== */}
      {receipt.services?.length > 0 && (
        <div className="px-6 sm:px-10 py-5 border-b border-gray-100">
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">
            {lang === 'ar' ? 'الخدمات' : 'Services'}
          </h3>
          <div className="rounded-2xl border border-gray-100 overflow-hidden">
            <table className="w-full" data-testid="services-table">
              <thead style={{ background: BRAND.primaryLight }}>
                <tr>
                  <th className={`text-xs font-bold uppercase tracking-wider text-gray-600 p-3 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {lang === 'ar' ? 'الخدمة' : 'Service'}
                  </th>
                  <th className={`text-xs font-bold uppercase tracking-wider text-gray-600 p-3 whitespace-nowrap ${isRTL ? 'text-left' : 'text-right'}`}>
                    {lang === 'ar' ? 'المبلغ' : 'Amount'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {receipt.services.map((s, i) => (
                  <tr key={i} className="border-t border-gray-100" data-testid={`service-row-${i}`}>
                    <td className="p-3">
                      <p className="text-sm font-semibold text-gray-800">
                        {lang === 'ar' ? (s.service_type_ar || s.service_type_en) : (s.service_type_en || s.service_type_ar)}
                      </p>
                      {s.description && (
                        <p className="text-xs text-gray-500 mt-0.5">{s.description}</p>
                      )}
                    </td>
                    <td className={`p-3 font-semibold text-gray-800 whitespace-nowrap ${isRTL ? 'text-left' : 'text-right'}`}>
                      {Number(s.amount || 0).toFixed(2)} <span className="text-xs text-gray-400">{currencyLabel}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== Totals ===== */}
      <div className="px-6 sm:px-10 py-5 border-b border-gray-100">
        <div className="space-y-2">
          <TotalRow
            label={lang === 'ar' ? 'المجموع الفرعي' : 'Subtotal'}
            value={`${Number(receipt.subtotal || 0).toFixed(2)} ${currencyLabel}`}
            testId="total-subtotal"
          />
          {receipt.tax_amount > 0 && (
            <TotalRow
              label={lang === 'ar' ? `الضريبة (${receipt.tax_percent || 5}%)` : `Tax (${receipt.tax_percent || 5}%)`}
              value={`${Number(receipt.tax_amount || 0).toFixed(2)} ${currencyLabel}`}
              testId="total-tax"
            />
          )}
          <div className="pt-3 mt-2 border-t border-dashed border-gray-200">
            <TotalRow
              label={lang === 'ar' ? 'الإجمالي' : 'Total'}
              value={`${Number(receipt.total || 0).toFixed(2)} ${currencyLabel}`}
              highlight
              testId="total-final"
            />
          </div>

          {partial && (
            <>
              <TotalRow
                label={lang === 'ar' ? 'المبلغ المدفوع' : 'Paid Amount'}
                value={`${Number(receipt.paid_amount || 0).toFixed(2)} ${currencyLabel}`}
                colorClass="text-sky-600"
                testId="total-paid"
              />
              <TotalRow
                label={lang === 'ar' ? 'المبلغ المتبقي' : 'Remaining'}
                value={`${Number(receipt.remaining_amount || 0).toFixed(2)} ${currencyLabel}`}
                colorClass="text-amber-600"
                testId="total-remaining"
              />
            </>
          )}
        </div>
      </div>

      {/* ===== Dates ===== */}
      <div className="px-6 sm:px-10 py-4 flex flex-col sm:flex-row items-center gap-3 sm:justify-between text-xs text-gray-500 border-b border-gray-100">
        <div className="flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5" />
          <span>{lang === 'ar' ? 'أُنشئت' : 'Created'}: {fmtDate(receipt.created_at)}</span>
        </div>
        {receipt.paid_at && (
          <div className="flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5 text-sky-500" />
            <span>{lang === 'ar' ? 'دُفعت' : 'Paid'}: {fmtDate(receipt.paid_at)}</span>
          </div>
        )}
      </div>

      {/* ===== Actions ===== */}
      <div className="px-6 sm:px-10 py-6 flex flex-col gap-3 print:hidden">
        {/* Primary: Download PDF */}
        <button
          onClick={downloadPdf}
          disabled={downloadingPdf}
          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-white transition-all hover:opacity-95 active:scale-[0.98] disabled:opacity-60 disabled:cursor-wait"
          style={{
            background: `linear-gradient(90deg, ${BRAND.primary} 0%, ${BRAND.accent} 100%)`,
            boxShadow: `0 10px 24px ${BRAND.primary}40`,
          }}
          data-testid="btn-download-pdf"
        >
          {downloadingPdf ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          {lang === 'ar' ? 'تحميل الإيصال / الفاتورة PDF' : 'Download receipt / invoice PDF'}
        </button>

        {/* Secondary row */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={openOfficial}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-white transition-all hover:opacity-95 active:scale-[0.98]"
            style={{ background: `linear-gradient(135deg, ${BRAND.accent} 0%, ${BRAND.accentDark} 100%)`, boxShadow: `0 8px 20px ${BRAND.accent}44` }}
            data-testid="btn-official-receipt"
          >
            <Sparkles className="h-4 w-4" />
            {lang === 'ar' ? 'الإيصال الرسمي الموحّد' : 'Official unified receipt'}
          </button>
          <button
            onClick={onPrint}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 transition-colors active:scale-[0.98]"
            data-testid="btn-print"
          >
            <Printer className="h-4 w-4" />
            {lang === 'ar' ? 'طباعة الإيصال' : 'Print receipt'}
          </button>
        </div>

        {/* Navigation row */}
        <div className="flex flex-col sm:flex-row gap-3 pt-1">
          <button
            onClick={onDashboard}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sky-700 bg-sky-50 hover:bg-sky-100 border border-sky-200 transition-colors active:scale-[0.98]"
            data-testid="btn-dashboard"
          >
            <LayoutDashboard className="h-4 w-4" />
            {lang === 'ar' ? 'العودة إلى لوحة التحكم' : 'Back to dashboard'}
          </button>
          <button
            onClick={onPayments}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-orange-700 bg-orange-50 hover:bg-orange-100 border border-orange-200 transition-colors active:scale-[0.98]"
            data-testid="btn-payments"
          >
            <FileText className="h-4 w-4" />
            {lang === 'ar' ? 'عرض سجل المدفوعات' : 'View payment history'}
          </button>
        </div>
      </div>

      {/* ===== iter-291 Official unified template modal ===== */}
      {showOfficial && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-2 sm:p-6 print:hidden"
          onClick={() => setShowOfficial(false)}
          data-testid="official-receipt-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100" style={{ background: 'linear-gradient(90deg, rgba(56,189,248,0.08) 0%, rgba(249,115,22,0.08) 100%)' }}>
              <div className="flex items-center gap-2 min-w-0">
                <Sparkles className="h-4 w-4 shrink-0" style={{ color: BRAND.accent }} />
                <h3 className="text-sm font-bold text-gray-800 truncate">
                  {lang === 'ar' ? 'الإيصال الرسمي الموحّد' : 'Official unified receipt'}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                {officialState === 'ready' && (
                  <button
                    onClick={printOfficial}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-700 text-xs font-semibold border border-gray-200"
                    data-testid="btn-print-official"
                  >
                    <Printer className="h-3.5 w-3.5" />
                    {lang === 'ar' ? 'طباعة' : 'Print'}
                  </button>
                )}
                <button
                  onClick={() => setShowOfficial(false)}
                  className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-600"
                  aria-label="close"
                  data-testid="btn-close-official"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden bg-gray-50">
              {officialState === 'loading' && (
                <div className="h-full flex items-center justify-center p-10 text-gray-500">
                  <Loader2 className="h-6 w-6 animate-spin mr-2" />
                  <span className="text-sm">
                    {lang === 'ar' ? 'جارٍ تحميل القالب الرسمي…' : 'Loading official template…'}
                  </span>
                </div>
              )}
              {officialState === 'error' && (
                <div className="h-full flex flex-col items-center justify-center p-10 text-center">
                  <AlertTriangle className="h-10 w-10 text-amber-500 mb-3" />
                  <p className="text-sm text-gray-700 max-w-sm">
                    {lang === 'ar'
                      ? 'تعذّر تحميل القالب الرسمي من مركز الإيصالات. يمكنك استخدام الإيصال الحالي أو إعادة المحاولة.'
                      : 'Could not load the official template from the Receipt Studio. Use the current receipt or try again.'}
                  </p>
                  <button
                    onClick={() => { setOfficialState('idle'); setOfficialHtml(null); loadOfficial(); }}
                    className="mt-4 flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    {lang === 'ar' ? 'إعادة المحاولة' : 'Retry'}
                  </button>
                </div>
              )}
              {officialState === 'ready' && officialHtml && (
                <iframe
                  title="official-receipt"
                  srcDoc={officialHtml}
                  sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                  className="w-full h-[80vh] bg-white"
                  data-testid="official-receipt-iframe"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ErrorState({ lang, icon, titleAr, titleEn, descAr, descEn, onDashboard, onPayments, onRetry, retryLabelAr, retryLabelEn, variant, testId }) {
  const isWarning = variant === 'warning';
  const iconColor = isWarning ? '#f59e0b' : '#ef4444';
  const iconBg = isWarning ? '#fef3c7' : '#fee2e2';

  return (
    <div className="p-10 sm:p-12 text-center" data-testid={testId}>
      <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center animate-in zoom-in-50 duration-500" style={{ background: iconBg, color: iconColor }}>
        <div className="[&>svg]:h-10 [&>svg]:w-10">{icon}</div>
      </div>
      <h2 className="mt-5 text-2xl font-bold text-gray-800">{lang === 'ar' ? titleAr : titleEn}</h2>
      <p className="mt-2 text-sm text-gray-500 leading-relaxed max-w-md mx-auto">
        {lang === 'ar' ? descAr : descEn}
      </p>
      <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 flex-wrap">
        {onRetry && (
          <button
            onClick={onRetry}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white transition-opacity hover:opacity-95"
            style={{ background: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryDark} 100%)` }}
            data-testid="btn-retry"
          >
            <RefreshCw className="h-4 w-4" />
            {lang === 'ar' ? (retryLabelAr || 'إعادة المحاولة') : (retryLabelEn || 'Retry')}
          </button>
        )}
        <button
          onClick={onDashboard}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sky-700 bg-sky-50 hover:bg-sky-100 border border-sky-200 transition-colors"
          data-testid="btn-dashboard"
        >
          <LayoutDashboard className="h-4 w-4" />
          {lang === 'ar' ? 'لوحة التحكم' : 'Dashboard'}
        </button>
        <button
          onClick={onPayments}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-orange-700 bg-orange-50 hover:bg-orange-100 border border-orange-200 transition-colors"
          data-testid="btn-payments"
        >
          <FileText className="h-4 w-4" />
          {lang === 'ar' ? 'سجل المدفوعات' : 'Payment history'}
        </button>
      </div>
    </div>
  );
}

function InfoField({ icon, label, value, dir, testId }) {
  return (
    <div className="min-w-0" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1 flex items-center gap-1">
        {icon}
        {label}
      </p>
      <p className="text-sm font-semibold text-gray-800 truncate" dir={dir || undefined}>{value}</p>
    </div>
  );
}

function TotalRow({ label, value, highlight, colorClass, testId }) {
  return (
    <div className="flex items-center justify-between" data-testid={testId}>
      <span className={`${highlight ? 'text-base font-bold text-gray-900' : 'text-sm text-gray-600'}`}>
        {label}
      </span>
      <span className={`${highlight ? 'text-lg font-extrabold' : 'text-sm font-semibold'} ${colorClass || 'text-gray-900'}`}>
        {value}
      </span>
    </div>
  );
}
